import _ from 'lodash';
import {createSelector} from 'reselect';

import {shouldPopout} from '../../../../../libs/shared/helpers/src/environment/platformHelpers';
import {tryParseVisualParticipantFromText} from '../../../../helpers/models/participantHelpers';
import {SchedulingLinkAvailabilityTypesEnum} from '../../../../models/api/schedulingLinkModel';
import {VisualParticipantModel} from '../../../../models/api/visualParticipantModel';
import {getFindTeammateAndOnlineStatusForVisualContact} from '../../../data/contacts/contactsSelectors';
import {getFlatSchedulingLinks} from '../../../data/schedulingLinks/schedulingLinksSelectors';
import {getUserNamespace} from '../../../data/teammates/teammatesSelectors';
import {getPanel} from '../../panelsSelectors';
import {PanelTypesEnum} from '../../panelStateBase';
import {makeGetCalendarsParameters} from '../calendarsPanelSelectors';
import {CalendarsViewTypesEnum} from '../calendarsParameters';

/*
 * Scheduling state.
 */

export const getIsSchedulingPanel = createSelector(
  makeGetCalendarsParameters(),
  (parameters) => parameters.viewType === CalendarsViewTypesEnum.SCHEDULING,
);

// TODO: Convert this to a selector creator, since it depends on props.
export const getSchedulingPanel = createSelector(getPanel, (panel) => {
  // A corresponding panel should exist, otherwise we have a problem.
  if (
    !panel ||
    panel.type !== PanelTypesEnum.CALENDARS ||
    panel.parameters.viewType !== CalendarsViewTypesEnum.SCHEDULING
  ) {
    throw new Error('The scheduling panel could not be found.');
  }

  return panel;
});

// Determines whether the scheduling panel was forced popped out in the browser.
// TODO: Convert this to a selector creator, since it depends on props.
export const getSchedulingPanelShouldForcePopout = createSelector(getSchedulingPanel, (schedulingPanel) =>
  Boolean(schedulingPanel.shouldForcePopout),
);

// Determines whether the scheduling panel is popped out into a child window.
// TODO: Convert this to a selector creator, since it depends on props.
// TODO: Check panel.popupAppPanelId when SchedulingLinkSettingsDialogContainer no longer uses inLegacyTransientPanel.
export const getSchedulingPanelIsPopout = createSelector(
  getSchedulingPanelShouldForcePopout,
  (shouldForcePopout) => shouldPopout(shouldForcePopout),
);

export const getSchedulingState = createSelector(getSchedulingPanel, (panel) => {
  if (!panel.scheduling) {
    throw new Error('The scheduling state could not be found.');
  }

  return panel.scheduling;
});

export const getDraftSchedulingLink = createSelector(
  getSchedulingState,
  (schedulingState) => schedulingState.draftLink,
);

export const getDidEditDraftSchedulingLink = createSelector(
  getSchedulingState,
  (schedulingState) => schedulingState.didEditDraft,
);

export const getSchedulingStatus = createSelector(
  getSchedulingState,
  (schedulingState) => schedulingState.status,
);

export const getSelectedStandardSlots = createSelector(getDraftSchedulingLink, (draftLink) =>
  draftLink.availability.type === SchedulingLinkAvailabilityTypesEnum.STANDARD
    ? draftLink.availability.blocks
    : [],
);

export const getSelectedRepeatingSlots = createSelector(getDraftSchedulingLink, (draftLink) =>
  draftLink.availability.type === SchedulingLinkAvailabilityTypesEnum.REPEATING
    ? draftLink.availability.blocks
    : [],
);

export const getSchedulingLinkId = createSelector(getDraftSchedulingLink, (draftLink) => draftLink.id);

export const getSchedulingCalendarVersionId = createSelector(
  getDraftSchedulingLink,
  (draftLink) => draftLink.calendarVersionId,
);

export const getDraftSchedulingLinkName = createSelector(
  getDraftSchedulingLink,
  (draftSchedulingLink) => draftSchedulingLink.name,
);

export const getDraftSchedulingLinkSlug = createSelector(
  getDraftSchedulingLink,
  (draftSchedulingLink) => draftSchedulingLink.slug,
);

export const getMeetingProperties = createSelector(
  getDraftSchedulingLink,
  (draftSchedulingLink) => draftSchedulingLink.meetingProperties,
);

export const getTrueAvailabilityStatus = createSelector(
  getSchedulingState,
  (schedulingState) => schedulingState.trueAvailabilityStatus,
);

export const getVisualParticipants = createSelector(
  [getMeetingProperties, getUserNamespace, getFindTeammateAndOnlineStatusForVisualContact],
  (meetingProperties, namespace, findTeammateAndOnlineStatusForVisualContact) =>
    _(meetingProperties.additionalGuests)
      .map((emailAddress) => tryParseVisualParticipantFromText(emailAddress, namespace))
      .compact()
      .map<VisualParticipantModel>((visualParticipant) => {
        const contactWithTeammateAndOnlineStatus =
          findTeammateAndOnlineStatusForVisualContact(visualParticipant);

        return {
          ...visualParticipant,
          ...contactWithTeammateAndOnlineStatus,
        };
      })
      .value(),
);

export const getDraftSchedulingLinkId = createSelector(
  getDraftSchedulingLink,
  (draftSchedulingLink) => draftSchedulingLink.id,
);

export const getOtherSlugs = createSelector(
  [getDraftSchedulingLinkId, getFlatSchedulingLinks],
  (draftSchedulingLinkId, schedulingLinks) =>
    schedulingLinks.filter((link) => link.id !== draftSchedulingLinkId).map((link) => link.slug),
);
